import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, Input } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { FileText, Receipt, Merge } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { useServiceDates, type ServiceDetail } from '@/api/services';
import { useCreateStatementInvoice } from '@/api/services-mutations';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { StatementCycleForm } from './statement-cycle-form';
import { StatementMergeConfigoptionsDialog } from './statement-merge-configoptions-dialog';
import { StatementConfigoptionPeekTable } from './statement-configoption-peek-table';

const muted = { color: 'var(--vn-fg-muted)' } as const;

/** A labelled range "start → end", or an em-dash placeholder. */
function rangeValue(start?: string | null, end?: string | null) {
  if (!start && !end) return <span style={muted}>—</span>;
  return `${formatDate(start)} → ${formatDate(end)}`;
}

/**
 * Statement (đối soát) tab — four sections:
 *   1. Period summary (existing) with Generate invoice action.
 *   2. Statement cycle editor — cycle radio + Update / Undo.
 *   3. Merge configoptions button → dialog.
 *   4. Read-only statementable configoption peek table (8 columns).
 *
 * Gated to SwiftFederation.* / Mlytics.Vnis / Cloud.VCloud templates
 * (caller is responsible for rendering this tab conditionally).
 */
export function ServiceStatementTab({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const toast = useToast();
  const dates = useServiceDates(id);
  const create = useCreateStatementInvoice(id);

  const [genOpen, setGenOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (dates.isError) return <TabError title="Failed to load statement dates" onRetry={() => dates.refetch()} />;
  if (dates.isLoading) return <TabLoading rows={4} />;

  const d = dates.data?.result;
  const hasDates =
    d && (d.statement_startdate || d.statement_enddate || d.first_startdate || d.current_startdate);

  const closeGenDialog = (next: boolean) => {
    if (create.isPending) return;
    if (!next) {
      setReason('');
      create.reset();
    }
    setGenOpen(next);
  };

  const confirmGenerate = () => {
    create.mutate(reason.trim(), {
      onSuccess: () => {
        toast.success('Statement invoice generated');
        closeGenDialog(false);
      },
      onError: (err) =>
        toast.error('Failed to generate statement invoice', {
          description: err instanceof Error ? err.message : undefined,
        }),
    });
  };

  const items: InfoItem[] = [
    { label: 'Statement period', value: rangeValue(d?.statement_startdate, d?.statement_enddate) },
    {
      label: 'Statement range',
      value: d?.statement_range != null ? `${d.statement_range} month(s)` : <span style={muted}>—</span>,
    },
    { label: 'First period', value: rangeValue(d?.first_startdate, d?.first_enddate) },
    { label: 'Current period', value: rangeValue(d?.current_startdate, d?.current_enddate) },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* 1. Period summary + Generate invoice */}
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="secondary" disabled={!hasDates} onClick={() => setGenOpen(true)}>
              <Receipt size={16} /> Generate statement invoice
            </Button>
          }
        >
          <CardTitle>Statement (đối soát)</CardTitle>
        </CardHeader>
        <CardBody>
          {hasDates ? (
            <InfoGrid items={items} />
          ) : (
            <EmptyState
              icon={<FileText size={24} />}
              title="No statement dates"
              description="This service has no statement cycle configured."
            />
          )}
        </CardBody>
      </Card>

      {/* 2. Cycle editor — Update + Undo */}
      <StatementCycleForm id={id} />

      {/* 3. Merge configoptions */}
      <Card flat>
        <CardHeader>
          <CardTitle>Configoptions</CardTitle>
        </CardHeader>
        <CardBody>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setMergeOpen(true)}
          >
            <Merge size={16} /> Merge configoptions
          </Button>
        </CardBody>
      </Card>

      {/* 4. Statementable peek table */}
      <StatementConfigoptionPeekTable id={id} currency={detail.service_currency || 'VND'} />

      {/* Generate invoice dialog */}
      <ConfirmDialog
        open={genOpen}
        onOpenChange={closeGenDialog}
        title="Generate statement invoice"
        description="Raise a statement (đối soát) invoice for the current statement period."
        confirmLabel="Generate"
        loading={create.isPending}
        onConfirm={confirmGenerate}
      >
        <Field label="Reason (optional)">
          {(p) => (
            <Input
              {...p}
              placeholder="Why is this statement being generated?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      </ConfirmDialog>

      {/* Merge configoptions dialog */}
      <StatementMergeConfigoptionsDialog
        id={id}
        open={mergeOpen}
        onOpenChange={setMergeOpen}
      />
    </div>
  );
}
